import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  Paper,
  Divider,
  Chip,
  CircularProgress
} from '@mui/material';
import { Matiere, Classe } from '../../types';
import CurriculumApiService from '../../services/curriculumApi';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import type { FieldChangeEvent } from '../../types/forms';
import { logger } from '../../lib/logger';

interface MatiereFormProps {
  matiere?: Matiere | null;
  defaultClasseId?: string;
  onSubmit: (data: Omit<Matiere, 'id'>) => void;
  onCancel: () => void;
}

const MatiereForm: React.FC<MatiereFormProps> = ({ matiere, defaultClasseId, onSubmit, onCancel }) => {
  const { environment } = useEnvironment();

  const [formData, setFormData] = useState<Omit<Matiere, 'id'>>({
    nom: '',
    nomCourt: '',
    description: '',
    code: '',
    classeId: defaultClasseId || undefined,
    ordre: 1,
    couleurTheme: '#3f51b5',
    icone: '',
    competencesClés: [],
    heuresParSemaine: undefined,
    coefficientExamen: undefined,
    dateCreation: new Date().toISOString().split('T')[0],
    dateModification: new Date().toISOString().split('T')[0],
    estActif: true,
    statut: 'draft' // Par défaut en brouillon
  });

  const [newCompetence, setNewCompetence] = useState('');
  const [classes, setClasses] = useState<Classe[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(false);

  // Charger les classes
  useEffect(() => {
    const loadClasses = async () => {
      try {
        setLoadingClasses(true);
        const classesData = await CurriculumApiService.getClasses(environment);
        setClasses(classesData);
      } catch (error) {
        logger.error('Erreur lors du chargement des classes:', error);
      } finally {
        setLoadingClasses(false);
      }
    };

    loadClasses();
  }, [environment]);

  useEffect(() => {
    if (matiere) {
      setFormData({
        nom: matiere.nom || '',
        nomCourt: matiere.nomCourt || '',
        description: matiere.description || '',
        code: matiere.code || '',
        classeId: matiere.classeId,
        ordre: matiere.ordre || 1,
        couleurTheme: matiere.couleurTheme || '#3f51b5',
        icone: matiere.icone || '',
        competencesClés: matiere.competencesClés || [],
        heuresParSemaine: matiere.heuresParSemaine,
        coefficientExamen: matiere.coefficientExamen,
        dateCreation: matiere.dateCreation || new Date().toISOString().split('T')[0],
        dateModification: new Date().toISOString().split('T')[0],
        estActif: matiere.estActif ?? true,
        statut: matiere.statut || 'draft',
        datePublication: matiere.datePublication,
        historiqueStatut: matiere.historiqueStatut
      });
    }
  }, [matiere]);

  const handleChange = (field: keyof Omit<Matiere, 'id'>) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    let value: string | number | boolean | undefined;

    if (event.target.type === 'checkbox') {
      value = (event.target as HTMLInputElement).checked;
    } else if (event.target.type === 'number') {
      // Convertir en nombre pour les champs numériques
      const numValue = event.target.value === '' ? undefined : Number(event.target.value);
      value = numValue;
    } else {
      value = event.target.value;
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSelectChange = (field: keyof Omit<Matiere, 'id'>) => (
    event: FieldChangeEvent
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleAddCompetence = () => {
    if (newCompetence.trim() && !formData.competencesClés.includes(newCompetence.trim())) {
      setFormData(prev => ({
        ...prev,
        competencesClés: [...prev.competencesClés, newCompetence.trim()]
      }));
      setNewCompetence('');
    }
  };

  const handleDeleteCompetence = (competence: string) => {
    setFormData(prev => ({
      ...prev,
      competencesClés: prev.competencesClés.filter(c => c !== competence)
    }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSubmit(formData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        {/* Informations de base */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Informations de base</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nom de la matière"
                  value={formData.nom}
                  onChange={handleChange('nom')}
                  required
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Nom court"
                  value={formData.nomCourt}
                  onChange={handleChange('nomCourt')}
                  required
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Code"
                  value={formData.code}
                  onChange={handleChange('code')}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={handleChange('description')}
                  multiline
                  rows={3}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Classe</InputLabel>
                  <Select
                    value={formData.classeId || ''}
                    onChange={handleSelectChange('classeId')}
                    label="Classe"
                    disabled={loadingClasses}
                  >
                    <MenuItem value="">
                      <em>Aucune classe</em>
                    </MenuItem>
                    {loadingClasses ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} />
                        <span style={{ marginLeft: 8 }}>Chargement...</span>
                      </MenuItem>
                    ) : (
                      classes.map((classe) => (
                        <MenuItem key={classe.id} value={classe.id}>
                          {classe.nom}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  label="Ordre"
                  type="number"
                  value={formData.ordre}
                  onChange={handleChange('ordre')}
                  required
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  label="Couleur thème"
                  type="color"
                  value={formData.couleurTheme}
                  onChange={handleChange('couleurTheme')}
                  required
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Icône"
                  value={formData.icone}
                  onChange={handleChange('icone')}
                  placeholder="📚"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Détails pédagogiques */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Détails pédagogiques</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Heures par semaine"
                  type="number"
                  value={formData.heuresParSemaine || ''}
                  onChange={handleChange('heuresParSemaine')}
                  inputProps={{ min: 0, step: 0.5 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Coefficient d'examen"
                  type="number"
                  value={formData.coefficientExamen || ''}
                  onChange={handleChange('coefficientExamen')}
                  inputProps={{ min: 0 }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Compétences clés */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Compétences clés</Typography>
            <Box display="flex" gap={1} mb={2}>
              <TextField
                label="Nouvelle compétence"
                value={newCompetence}
                onChange={(e) => setNewCompetence(e.target.value)}
                onKeyPress={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleAddCompetence();
                  }
                }}
                size="small"
                sx={{ flexGrow: 1 }}
              />
              <Button onClick={handleAddCompetence} variant="outlined">
                Ajouter
              </Button>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {formData.competencesClés.map((competence, index) => (
                <Chip
                  key={index}
                  label={competence}
                  onDelete={() => handleDeleteCompetence(competence)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Statut et activation */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Statut et activation</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={formData.statut || 'draft'}
                    onChange={handleSelectChange('statut')}
                    required
                  >
                    <MenuItem value="draft">🔸 Brouillon</MenuItem>
                    <MenuItem value="published">✅ Publié</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.estActif}
                      onChange={(e) => setFormData(prev => ({ ...prev, estActif: e.target.checked }))}
                    />
                  }
                  label="Matière active"
                />
                <Typography variant="body2" color="textSecondary">
                  Une matière inactive ne sera pas visible dans l'application.
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button onClick={onCancel}>
          Annuler
        </Button>
        <Button type="submit" variant="contained">
          {matiere ? 'Mettre à jour' : 'Créer'}
        </Button>
      </Box>
    </Box>
  );
};

export default MatiereForm;
